import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Skeleton, Empty } from 'antd';
import { CompensationSentence } from 'types';

import history from 'app-router/history';
import { useIntl } from 'common/config/i18n';
import { compensationSentencesActions, compensationSentencesSelectors, UserInfoSelectors } from 'common/config/store';
import { StyledMainTitle, StyledListUtil } from 'common/styling/mixins';
import Layout from 'components/templates/layout';
import StyledTable from 'components/organisms/styled-table';
import ListModifier from 'components/organisms/list-modifier';
import { htmlParser } from 'common/utils/html-parser';

const { StyledListWrapper, StyledListTitleWrapper } = StyledListUtil;

const CompensationSentences = () => {
  const status = useSelector(compensationSentencesSelectors.statusSelector);
  const compensationSentencesData = useSelector(compensationSentencesSelectors.dataSelector);
  const canWriteCompensationSentences = useSelector(UserInfoSelectors.hasWriteAccess('Help-Info'));
  const { t } = useIntl();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(compensationSentencesActions.fetchCompensationSentences());
  }, [dispatch]);

  const onClickEditHandler = (compensationSentence: object): void => {
    const cs = compensationSentence as CompensationSentence;
    return history.push(`/compensation-sentence/${cs.id}`);
  };

  const columns = [
    {
      title: t('table', 'COLUMN_COMPENSATION_SENTENCES_NAME'),
      dataIndex: 'name',
      key: 'name',
      width: '18%',
    },
    {
      title: t('table', 'COLUMN_COMPENSATION_SENTENCES_LONG_MSG'),
      key: 'longMessage',
      dataIndex: 'longMessage',
      width: '64%',
      render: (longMessage: string) => {
        return <div className="ql-editor">{htmlParser(longMessage)}</div>;
      },
    },
    {
      title: t('table', 'COLUMN_EDIT'),
      key: 'modifier',
      width: '18%',
      shouldBeAdmin: true,
      render: (record: CompensationSentence) => {
        return (
          <ListModifier
            hideDeleteButton
            hideEditButton={!canWriteCompensationSentences}
            onEdit={() => onClickEditHandler?.(record)}
          />
        );
      },
    },
  ];

  const tableData = compensationSentencesData || [];

  return (
    <div>
      <Layout />
      <StyledListWrapper>
        <StyledListTitleWrapper>
          <StyledMainTitle>{t('CompensationSentencePage', 'TITLE')}</StyledMainTitle>
        </StyledListTitleWrapper>
        {status === 'loading' && <Skeleton />}
        {status === 'success' && (
          <StyledTable
            textAlign="center"
            pagination={false}
            dataSource={tableData}
            columns={canWriteCompensationSentences ? columns : columns.filter((column) => !column.shouldBeAdmin)}
            rowKey="id"
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('empty')} /> }}
          />
        )}
      </StyledListWrapper>
    </div>
  );
};

export default CompensationSentences;
